<template>
  <v-container fluid class="pa-0">
    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="d-flex flex-wrap align-center">
            <div>
              <div class="text-h6">PCs monitoreadas</div>
              <div class="text-caption grey--text text--darken-1">
                Handshake al endpoint <code>/api/PcHeartbeat/Handshake</code>
              </div>
            </div>
            <v-spacer />
            <v-text-field
              v-model.number="offlineAfterSeconds"
              label="Offline después de (seg)"
              type="number"
              dense
              outlined
              hide-details
              class="mr-3"
              style="max-width: 220px;"
              @change="loadEquipos"
            />
            <v-btn color="primary" @click="loadEquipos" :loading="loading">
              Actualizar
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-row class="mb-2">
              <v-col cols="12" md="4">
                <v-sheet color="green lighten-5" class="pa-4 rounded">
                  <div class="text-caption text-uppercase">Online</div>
                  <div class="text-h5 font-weight-bold green--text text--darken-2">{{ onlineCount }}</div>
                </v-sheet>
              </v-col>
              <v-col cols="12" md="4">
                <v-sheet color="red lighten-5" class="pa-4 rounded">
                  <div class="text-caption text-uppercase">Offline</div>
                  <div class="text-h5 font-weight-bold red--text text--darken-2">{{ offlineCount }}</div>
                </v-sheet>
              </v-col>
              <v-col cols="12" md="4">
                <v-sheet color="blue lighten-5" class="pa-4 rounded">
                  <div class="text-caption text-uppercase">Total</div>
                  <div class="text-h5 font-weight-bold blue--text text--darken-2">{{ equipos.length }}</div>
                </v-sheet>
              </v-col>
            </v-row>

            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Buscar por nombre, key, host o IP"
              single-line
              hide-details
              dense
              outlined
              class="mb-4"
            />

            <v-alert v-if="error" dense outlined type="error" class="mb-4">
              {{ error }}
            </v-alert>

            <v-data-table
              :headers="headers"
              :items="equipos"
              :search="search"
              :loading="loading"
              loading-text="Cargando equipos..."
              class="elevation-0"
            >
              <template v-slot:item.status="{ item }">
                <v-chip small dark :color="item.online ? 'green' : 'red'">
                  {{ item.status }}
                </v-chip>
              </template>

              <template v-slot:item.displayName="{ item }">
                <div class="font-weight-medium">{{ item.displayName || item.machineKey }}</div>
                <div class="text-caption grey--text">{{ item.machineKey }}</div>
              </template>

              <template v-slot:item.lastSeenUtc="{ item }">
                <div>{{ item.lastSeenUtc }}</div>
                <div class="text-caption grey--text">hace {{ formatElapsed(item.secondsSinceLastSeen) }}</div>
              </template>

              <template v-slot:item.network="{ item }">
                <div>{{ item.localIp || '-' }}</div>
                <div class="text-caption grey--text">{{ item.publicIp || '-' }}</div>
              </template>

              <template v-slot:item.agent="{ item }">
                <div>{{ item.agentVersion || '-' }}</div>
                <div class="text-caption grey--text">{{ item.intervalSeconds || 60 }} seg</div>
              </template>

              <template v-slot:item.diagnostic="{ item }">
                <div>{{ item.probableCause || '-' }}</div>
                <div class="text-caption grey--text">
                  {{ item.diagnosticSummary || '-' }}
                </div>
                <div
                  v-if="item.lastClientFailureUtc || item.lastFailureType || item.lastFailureStatusCode"
                  class="text-caption grey--text"
                >
                  {{ buildFailureLine(item) }}
                </div>
              </template>

              <template v-slot:item.system="{ item }">
                <div>{{ item.hostName || '-' }}</div>
                <div class="text-caption grey--text">{{ item.userName || '-' }}</div>
              </template>

              <template v-slot:item.notes="{ item }">
                <span class="text-caption">{{ item.notes || '-' }}</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      loading: false,
      error: "",
      search: "",
      offlineAfterSeconds: 180,
      equipos: [],
      refreshHandle: null,
      headers: [
        { text: "Estatus", value: "status", sortable: false, width: 120 },
        { text: "Equipo", value: "displayName" },
        { text: "Host / Usuario", value: "system", sortable: false },
        { text: "Red local / pública", value: "network", sortable: false },
        { text: "Agente", value: "agent", sortable: false },
        { text: "Diagnóstico", value: "diagnostic", sortable: false },
        { text: "Último handshake (UTC)", value: "lastSeenUtc" },
        { text: "Notas", value: "notes", sortable: false }
      ]
    };
  },
  computed: {
    onlineCount() {
      return this.equipos.filter(x => x.online).length;
    },
    offlineCount() {
      return this.equipos.filter(x => !x.online).length;
    }
  },
  methods: {
    initialize() {
      this.loadEquipos();
      this.startAutoRefresh();
    },
    async loadEquipos() {
      this.loading = true;
      this.error = "";

      try {
        const { data } = await axios.get("/PcHeartbeat/Equipos", {
          params: {
            offlineAfterSeconds: this.offlineAfterSeconds
          }
        });

        this.equipos = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error al cargar heartbeat de PCs:", error);
        this.error = "No fue posible cargar el estatus de las PCs.";
      } finally {
        this.loading = false;
      }
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.refreshHandle = setInterval(() => {
        this.loadEquipos();
      }, 30000);
    },
    stopAutoRefresh() {
      if (this.refreshHandle) {
        clearInterval(this.refreshHandle);
        this.refreshHandle = null;
      }
    },
    formatElapsed(seconds) {
      if (seconds == null || Number.isNaN(seconds)) return "-";
      if (seconds < 60) return `${seconds}s`;

      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m`;

      const hours = Math.floor(minutes / 60);
      const remMinutes = minutes % 60;
      return remMinutes > 0 ? `${hours}h ${remMinutes}m` : `${hours}h`;
    },
    buildFailureLine(item) {
      const parts = [];

      if (item.lastClientFailureUtc) {
        parts.push(`Fallo: ${item.lastClientFailureUtc}`);
      }

      if (item.lastFailureType) {
        parts.push(`tipo ${item.lastFailureType}`);
      }

      if (item.lastFailureStatusCode) {
        parts.push(`HTTP ${item.lastFailureStatusCode}`);
      }

      return parts.join(" | ");
    }
  },
  created() {
    this.initialize();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  }
};
</script>
